import { observer } from "mobx-react-lite";
import { FC } from "react";

import { compareDatesPredicate, DATE_FORMATS, DateTime } from "@bps/date-time";
import {
  DetailsListLayoutMode,
  IColumn,
  Stack,
  Text,
  useTheme
} from "@bps/fluent-ui";
import {
  ClinicalReminderCommContactRecordDto,
  ClinicalReminderCommReplyDto
} from "@libs/gateways/clinical/ClinicalGateway.dtos.ts";
import { OutboundCommContentDto } from "@libs/gateways/comms/CommsGateway.dtos.ts";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import { User } from "@stores/core/models/User.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { DataFetcher } from "@ui-components/data-fetcher/DataFetcher.tsx";
import { ShimmeredDetailsList } from "@ui-components/ShimmeredDetailsList/ShimmeredDetailsList.tsx";
import { UserFetcher } from "@ui-components/UserFetcher.tsx";
import { When } from "@ui-components/withPerm.tsx";

import { FollowupReminderCurrentStatusBadge } from "./FollowupReminderCurrentStatusBadge.tsx";
import { ClinicalActivityTableRow } from "./types/clinical-activity-table.type.ts";

type FollowupReminderCurrentStatusProps = {
  reminder: ClinicalActivityTableRow;
};
interface hoveredOutboundCommItems {
  commContactRecord: ClinicalReminderCommContactRecordDto;
  contactInfo?: string;
}
export const FollowupReminderCurrentStatus: FC<FollowupReminderCurrentStatusProps> =
  observer(({ reminder }) => {
    const { comms, core } = useStores();
    const theme = useTheme();

    if (reminder.clinicalReminderComm?.contactRecords) {
      const sortedContactRecords = Array.from(
        reminder.clinicalReminderComm.contactRecords
      ).sort((a, b) =>
        compareDatesPredicate(
          DateTime.fromISO(b.timeStamp),
          DateTime.fromISO(a.timeStamp)
        )
      );

      const latestContactRecord = sortedContactRecords[0];

      if (!latestContactRecord) {
        return null;
      }

      const multipleReplyRecords =
        sortedContactRecords.filter(x => x.replies && x.replies.length > 0)
          .length > 1;

      const latestReply = latestContactRecord.replies?.reduce(
        (latest: ClinicalReminderCommReplyDto | undefined, reply) => {
          if (!latest) return reply;
          if (
            DateTime.fromISO(latest.timeStamp) <
            DateTime.fromISO(reply.timeStamp)
          )
            return reply;
          return latest;
        }
      );

      const validReplyStatuses = ["SENT", "DELIVERED"];

      const showReply = validReplyStatuses.includes(
        reminder.clinicalReminderComm.reminderStatus
      );

      const outboundCommIds = sortedContactRecords.map(x => x.outboundCommId);

      const hoverCardColumns: IColumn[] = [
        {
          name: "Sent/Contacted",
          key: "date",
          minWidth: 150,
          maxWidth: 150,
          onRender: (item: hoveredOutboundCommItems) => (
            <Stack
              styles={{
                root: { justifyContent: "start", height: "100%" }
              }}
            >
              <Text>
                {DateTime.fromISO(item.commContactRecord.timeStamp).toFormat(
                  DATE_FORMATS.LONG_DATE_TIME_FORMAT
                )}
              </Text>
            </Stack>
          )
        },
        {
          name: "Status",
          key: "sentStatus",
          minWidth: 90,
          maxWidth: 150,

          onRender: (item: hoveredOutboundCommItems, index: number) => {
            const sentOrder = sortedContactRecords.length - index;
            return (
              <FollowupReminderCurrentStatusBadge
                reminder={reminder}
                contactRecord={item.commContactRecord}
                sortedContactRecords={sortedContactRecords}
                itemNumber={sentOrder}
              />
            );
          }
        }
      ];

      if (core.hasPermissions(Permission.ClinicalReminderReplyAllowed)) {
        hoverCardColumns.push({
          name: "Details",
          key: "details",
          minWidth: 250,
          isMultiline: true,
          onRender: (item: hoveredOutboundCommItems) => {
            if (item.commContactRecord.replies) {
              return (
                <Stack tokens={{ childrenGap: 8 }}>
                  {Array.from(item.commContactRecord.replies)
                    .sort((a, b) =>
                      compareDatesPredicate(
                        DateTime.fromISO(b.timeStamp),
                        DateTime.fromISO(a.timeStamp)
                      )
                    )
                    .map(x => {
                      return (
                        <Stack key={x.outboundCommReplyId}>
                          <Text
                            variant="small"
                            styles={{
                              root: { color: theme.palette.neutralSecondary }
                            }}
                          >
                            {DateTime.fromISO(x.timeStamp).toFormat(
                              DATE_FORMATS.LONG_DATE_TIME_FORMAT
                            )}
                          </Text>
                          <Text variant="medium">{x.replyContent}</Text>
                        </Stack>
                      );
                    })}
                </Stack>
              );
            }

            if (
              item.commContactRecord.status === "CONTACTED" &&
              item.commContactRecord.userActioned
            ) {
              return (
                <UserFetcher userId={item.commContactRecord.userActioned}>
                  {(user: User) => (
                    <Text
                      styles={{
                        root: { color: theme.palette.neutralSecondary }
                      }}
                    >
                      by {user.fullName}
                    </Text>
                  )}
                </UserFetcher>
              );
            }

            return "No reply";
          }
        });
      }

      hoverCardColumns.push({
        name: "",
        key: "contactInfo",
        minWidth: 200,
        maxWidth: 300,
        onRender: (item: hoveredOutboundCommItems) => {
          if (item.commContactRecord.status === "CONTACTED") {
            return null;
          }

          return (
            <Stack
              styles={{
                root: { justifyContent: "start", height: "100%" }
              }}
              horizontal
            >
              <Text
                variant="medium"
                styles={{
                  root: {
                    color: theme.palette.neutralSecondary,
                    paddingRight: 4
                  }
                }}
              >
                {item.commContactRecord.contactType}:
              </Text>
              <Text> {item?.contactInfo}</Text>
            </Stack>
          );
        }
      });

      return (
        <Stack tokens={{ childrenGap: 4 }}>
          <Text>
            {DateTime.fromISO(latestContactRecord.timeStamp).toFormat(
              DATE_FORMATS.LONG_DATE_TIME_FORMAT
            )}
          </Text>
          <Stack
            horizontal
            tokens={{ childrenGap: 4 }}
            styles={{ root: { textAlign: "center", alignItems: "center" } }}
          >
            <FollowupReminderCurrentStatusBadge
              reminder={reminder}
              contactRecord={latestContactRecord}
              sortedContactRecords={sortedContactRecords}
              itemNumber={sortedContactRecords.length}
              showRepliesForCollection
              useHoverCard
              onRenderPlainCard={() => (
                <Stack
                  styles={{
                    root: {
                      padding: 16
                    }
                  }}
                >
                  <DataFetcher<OutboundCommContentDto[]>
                    fetch={async () => {
                      return await comms.getOutboundCommContents({
                        outboundCommIds
                      });
                    }}
                    noExceptionsHandlers
                  >
                    {(data = [], loading, error) => {
                      const items = sortedContactRecords.map(x => {
                        const item: hoveredOutboundCommItems = {
                          commContactRecord: x,
                          contactInfo: data.find(
                            content =>
                              content.outboundCommId === x.outboundCommId
                          )?.channelProperties
                        };
                        return item;
                      });
                      return (
                        <Stack
                          tokens={{ childrenGap: 4 }}
                          styles={{
                            root: { height: loading ? 150 : undefined }
                          }}
                        >
                          <Text
                            variant="large"
                            styles={{
                              root: {
                                paddingBottom: 12
                              }
                            }}
                          >
                            Previous stages
                          </Text>

                          <ShimmeredDetailsList
                            enableShimmer={loading}
                            errorMessage={error?.message}
                            key="reminder-detailed-contact-records"
                            columns={hoverCardColumns}
                            items={items}
                            layoutMode={DetailsListLayoutMode.justified}
                          />
                        </Stack>
                      );
                    }}
                  </DataFetcher>
                </Stack>
              )}
            />
            <When permission={Permission.ClinicalReminderReplyAllowed}>
              {showReply && latestReply && !multipleReplyRecords && (
                <>
                  <Text
                    variant="small"
                    styles={{ root: { color: theme.palette.neutralSecondary } }}
                  >
                    Reply:
                  </Text>
                  <Text variant="small" nowrap>
                    {latestReply?.replyContent}
                  </Text>
                </>
              )}

              {showReply && multipleReplyRecords && (
                <Text
                  variant="small"
                  styles={{ root: { color: theme.palette.neutralSecondary } }}
                >
                  Multiple replies
                </Text>
              )}
              {showReply && !multipleReplyRecords && !latestReply && (
                <Text
                  variant="small"
                  styles={{ root: { color: theme.palette.neutralSecondary } }}
                >
                  No reply
                </Text>
              )}
            </When>
          </Stack>
        </Stack>
      );
    }

    return null;
  });
